import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export {
  captureAssetDownload,
  downloadOrShareAsset,
  getProxiedUrl,
  isSameOrigin,
} from '@/lib/asset-download';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isEditableShortcutTarget(
  target: EventTarget | null,
  options?: { allowCommandInput?: boolean }
) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const isCommandInput =
    options?.allowCommandInput && Boolean(target.closest('[cmdk-input]'));
  const isEditableElement =
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable ||
    Boolean(target.closest('input')) ||
    Boolean(target.closest('textarea')) ||
    Boolean(target.closest('select')) ||
    Boolean(target.closest('[contenteditable="true"]'));

  return isEditableElement && !isCommandInput;
}

function parseRgbStringToNormalized(
  rgb: string
): [number, number, number] | null {
  const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!match) return null;
  const r = Math.min(255, Math.max(0, parseInt(match[1]!, 10)));
  const g = Math.min(255, Math.max(0, parseInt(match[2]!, 10)));
  const b = Math.min(255, Math.max(0, parseInt(match[3]!, 10)));
  return [r / 255, g / 255, b / 255];
}
export function getShadcnPrimaryColorNormalized(): [number, number, number] {
  if (typeof window === 'undefined' || !window.document?.body) {
    return [0.5, 0.5, 0.5];
  }
  try {
    const probe = document.createElement('span');
    probe.style.color = 'hsl(var(--primary))';
    probe.style.position = 'absolute';
    probe.style.opacity = '0';
    document.body.appendChild(probe);
    const color = getComputedStyle(probe).color;
    document.body.removeChild(probe);
    const parsed = parseRgbStringToNormalized(color);
    if (parsed) return parsed;
  } catch {}
  return [0.5, 0.5, 0.5];
}

/**
 * Safely access localStorage without causing Next.js SSR issues.
 * Based on: https://github.com/vercel/next.js/discussions/84512
 */
function getLocalStorage(): Storage | null {
  if (typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

export function safeLocalStorageGetItem(key: string): string | null {
  const storage = getLocalStorage();
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

export function safeLocalStorageSetItem(key: string, value: string): boolean {
  const storage = getLocalStorage();
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function safeLocalStorageRemoveItem(key: string): boolean {
  const storage = getLocalStorage();
  if (!storage) return false;
  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safely access sessionStorage without causing Next.js SSR issues.
 * Based on: https://github.com/vercel/next.js/discussions/84512
 */
function getSessionStorage(): Storage | null {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    return window.sessionStorage;
  }
  return null;
}

export function safeSessionStorageGetItem(key: string): string | null {
  const storage = getSessionStorage();
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

export function safeSessionStorageSetItem(key: string, value: string): boolean {
  const storage = getSessionStorage();
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function safeSessionStorageRemoveItem(key: string): boolean {
  const storage = getSessionStorage();
  if (!storage) return false;
  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * Result of validating a URL for accessibility
 */
export type UrlValidationResult = {
  isValid: boolean;
  isAccessible: boolean;
  error?: string;
};

/**
 * Validates that a URL is properly formatted and accessible.
 * Uses a HEAD request with timeout to check accessibility without downloading the full content.
 *
 * @param url - The URL to validate
 * @param timeoutMs - Timeout in milliseconds for the accessibility check (default: 10000)
 * @returns Promise<UrlValidationResult> - Validation result with accessibility status
 */
export async function validateImageUrl(
  url: string | null | undefined,
  timeoutMs: number = 10000
): Promise<UrlValidationResult> {
  // Check if URL is provided
  if (!url || typeof url !== 'string' || url.trim().length === 0) {
    return {
      isValid: false,
      isAccessible: false,
      error: 'No URL provided',
    };
  }

  const trimmedUrl = url.trim();

  // Check basic URL format
  if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
    return {
      isValid: false,
      isAccessible: false,
      error: 'Invalid URL format: must start with http:// or https://',
    };
  }

  // Validate URL can be parsed
  try {
    new URL(trimmedUrl);
  } catch {
    return {
      isValid: false,
      isAccessible: false,
      error: 'Invalid URL format: cannot be parsed',
    };
  }

  // Check accessibility with HEAD request
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    // Try HEAD first (faster, no content download)
    const response = await fetch(trimmedUrl, {
      method: 'HEAD',
      signal: controller.signal,
      mode: 'cors',
      credentials: 'omit',
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Some servers don't support HEAD, try GET with abort
      if (response.status === 405) {
        const getController = new AbortController();
        const getTimeoutId = setTimeout(() => getController.abort(), timeoutMs);

        try {
          const getResponse = await fetch(trimmedUrl, {
            method: 'GET',
            signal: getController.signal,
            mode: 'cors',
            credentials: 'omit',
          });

          clearTimeout(getTimeoutId);
          getController.abort(); // Cancel the download immediately

          if (!getResponse.ok) {
            return {
              isValid: true,
              isAccessible: false,
              error: `URL returned HTTP ${getResponse.status}`,
            };
          }

          return {
            isValid: true,
            isAccessible: true,
          };
        } catch (getError) {
          clearTimeout(getTimeoutId);
          if (getError instanceof Error && getError.name === 'AbortError') {
            // If aborted after getting response, it's accessible
            return {
              isValid: true,
              isAccessible: true,
            };
          }
          return {
            isValid: true,
            isAccessible: false,
            error: 'Unable to verify URL accessibility',
          };
        }
      }

      return {
        isValid: true,
        isAccessible: false,
        error: `URL returned HTTP ${response.status}`,
      };
    }

    return {
      isValid: true,
      isAccessible: true,
    };
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        return {
          isValid: true,
          isAccessible: false,
          error:
            'URL validation timed out - the image may be too large or the server is slow',
        };
      }

      // CORS errors usually mean the resource exists but we can't access it directly
      // The AI model will be able to access it server-side
      if (
        error.message.includes('CORS') ||
        error.message.includes('Failed to fetch')
      ) {
        // For CORS errors, we assume the URL is valid but we can't verify from the browser
        // Return a special state indicating we can't verify but it might work
        return {
          isValid: true,
          isAccessible: true, // Assume accessible - AI can likely access it
        };
      }

      return {
        isValid: true,
        isAccessible: false,
        error: `Network error: ${error.message}`,
      };
    }

    return {
      isValid: true,
      isAccessible: false,
      error: 'Unknown error occurred while validating URL',
    };
  }
}
